import styled, { css } from 'styled-components';
import { baseVariables } from '../../styles';
import { OCardFeatureContentAlignment, OCardFeatureImageScale } from './OCardFeature.types';

const { spacers, mediaQuery } = baseVariables;

const spacer = (index: number, fallback: string): string => spacers?.spacers?.[index] ?? fallback;

/**
 * Per-breakpoint aspect ratio for the Modal Video thumbnail box, mirroring the
 * image rendition matrix in `index.tsx` (`IMAGE_SCALE_ASPECT_RATIO`): mobile is
 * the base value, tablet applies at `md`, and desktop applies at `lg`.
 */
const THUMBNAIL_BOX_ASPECT_RATIO: Record<OCardFeatureImageScale, { mobile: string; tablet: string; desktop: string }> =
  {
    [OCardFeatureImageScale.L]: { mobile: '1 / 1', tablet: '1 / 1', desktop: '1 / 1' },
    [OCardFeatureImageScale.M]: { mobile: '3 / 2', tablet: '1 / 1', desktop: '3 / 2' },
    [OCardFeatureImageScale.S]: { mobile: '16 / 9', tablet: '1 / 1', desktop: '16 / 9' },
  };

const thumbnailBoxAspectRatioCss = (imageScale?: OCardFeatureImageScale) => {
  if (!imageScale) {
    return null;
  }
  const ratios = THUMBNAIL_BOX_ASPECT_RATIO[imageScale];
  return css`
    [data-component-name='m-web-ui-kit-MVideoThumbnail'] {
      aspect-ratio: ${ratios.mobile};

      @media ${mediaQuery.md} {
        aspect-ratio: ${ratios.tablet};
      }

      @media ${mediaQuery.lg} {
        aspect-ratio: ${ratios.desktop};
      }
    }
  `;
};

export const Wrapper = styled.article`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  inline-size: 100%;
  overflow: hidden;
  color: inherit;
  background-color: var(--t-brand-bg-color);
  border-radius: var(--t-cards-border-radius);
  box-shadow: 0 0.25rem 1.25rem rgba(0, 0, 0, 0.07);

  /* Desktop and Tablet use a horizontal layout. Mobile (below md) always
     stacks vertically with the media on top, regardless of the layout prop. */
  @media ${mediaQuery.md} {
    flex-direction: row;
  }
`;

export const MediaColumn = styled.div<{
  $thumbnailAspectRatioScale?: OCardFeatureImageScale;
}>`
  display: flex;
  flex-direction: column;
  inline-size: 100%;
  /*
   * Mobile only: media always stacks on top, regardless of the DOM order used
   * for the desktop/tablet layout. This is the single intentional visual/DOM
   * reordering; it only reorders the two top-level blocks in the stacked
   * (column) layout and does not change the interactive sequence meaning.
   */
  order: -1;

  /*
   * Within OCardFeature only the card's own border radius should show. Neutralize
   * the border radius that MMediaSelector and the media containers it renders
   * apply by default (MMediaSelector wrapper, MVideoThumbnail wrapper, and the
   * AVideo player wrappers shown on play). Targeted by data attribute/class so
   * round controls inside the media (play button overlay, icon buttons, tags)
   * keep their own radius, and other consumers of these components are
   * unaffected.
   */
  [data-component-name='m-web-ui-kit-MMediaSelector'],
  [data-component-name='m-web-ui-kit-MVideoThumbnail'],
  [data-component-name='a-web-ui-kit-AVideo'],
  .a-video {
    border-radius: 0;
  }

  /* Desktop/Tablet: horizontal layout follows DOM order (no CSS order swap).*/
  @media ${mediaQuery.md} {
    box-sizing: border-box;
    flex: 1 1 50%;
    min-inline-size: 0;
    order: 0;
  }

  /*
   * Modal Video only: reshape the thumbnail box per breakpoint to match the
   * responsive image renditions.
   */
  ${({ $thumbnailAspectRatioScale }) => thumbnailBoxAspectRatioCss($thumbnailAspectRatioScale)}
`;

export const ContentColumn = styled.div<{
  $contentAlignment: OCardFeatureContentAlignment;
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${spacer(4, '1.5rem')};
  padding-inline: ${spacer(4, '1.5rem')};
  /* spacer-05 (32px) block padding at every breakpoint. */
  padding-block: ${spacer(5, '2rem')};
  align-items: ${({ $contentAlignment }) =>
    $contentAlignment === OCardFeatureContentAlignment.Centered ? 'center' : 'flex-start'};
  text-align: ${({ $contentAlignment }) =>
    $contentAlignment === OCardFeatureContentAlignment.Centered ? 'center' : 'start'};

  /* Tablet */
  @media ${mediaQuery.md} {
    box-sizing: border-box;
    flex: 1 1 50%;
    min-inline-size: 0;
    padding-inline: ${spacer(5, '2rem')};
  }

  /* Desktop */
  @media ${mediaQuery.lg} {
    padding-inline: ${spacer(6, '3rem')};
  }
`;

export const ButtonRow = styled.div<{ $contentAlignment: OCardFeatureContentAlignment }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacer(3, '1rem')};
  inline-size: 100%;
  justify-content: ${({ $contentAlignment }) =>
    $contentAlignment === OCardFeatureContentAlignment.Centered ? 'center' : 'flex-start'};
`;
